import * as THREE from 'three';

// Rein optische Halo-Textur hinter der Sonnenkugel (additiv geblendet, kein echtes
// Postprocessing/Volumetric-God-Rays — güngstig und schattet daher auch nichts ab,
// beeinflusst also weder Tracking noch Energieberechnung, die komplett eigene,
// von diesem Mesh unabhängige Vektoren/Raycasts nutzen, siehe SunTracker/EnergyTracker).
function makeGlowTexture(): THREE.Texture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,244,214,0.95)');
  grad.addColorStop(0.25, 'rgba(255,220,140,0.55)');
  grad.addColorStop(0.55, 'rgba(255,190,90,0.18)');
  grad.addColorStop(1, 'rgba(255,180,80,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

export class Sun {
  light: THREE.DirectionalLight;
  mesh: THREE.Mesh;
  glow: THREE.Sprite;

  constructor(textureLoader: THREE.TextureLoader) {
    this.light = new THREE.DirectionalLight(0xffffff, 1);
    this.light.castShadow = true;
    this.light.shadow.mapSize.set(2048, 2048);
    this.light.shadow.camera.left = -500;
    this.light.shadow.camera.right = 500;
    this.light.shadow.camera.top = 500;
    this.light.shadow.camera.bottom = -500;
    this.light.shadow.camera.near = 1;
    // Muss die Distanz Licht->Szeneninhalt abdecken. Die Sonne kreist jetzt auf einem
    // deutlich größeren Radius (siehe SunTracker-Konfiguration in setupScene.ts), sonst
    // reicht der Schattenfrustum nicht mehr bis zum Ursprung zurück und Schatten verschwinden.
    this.light.shadow.camera.far = 6000;

    const sunTexture = textureLoader.load('/textures/sun_texture.png');
    // Größer und weiter draußen (siehe radius in setupScene.ts), damit sie bei tiefem
    // Sonnenstand jenseits des jetzt 2500x2500 großen Terrains bleibt, statt optisch
    // in den Boden zu schneiden.
    const geometry = new THREE.SphereGeometry(220, 32, 32);
    const material = new THREE.MeshBasicMaterial({
      map: sunTexture,
      color: new THREE.Color(0xffcc66),
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    });

    this.mesh = new THREE.Mesh(geometry, material);

    // "Sonnenstrahlen"-Glow: additiv geblendetes Sprite, immer zur Kamera ausgerichtet,
    // deutlich größer als die Kugel selbst für einen weichen Strahlenkranz.
    const glowMat = new THREE.SpriteMaterial({
      map: makeGlowTexture(),
      color: 0xffffff,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.glow = new THREE.Sprite(glowMat);
    this.glow.scale.set(1400, 1400, 1);
    // Kind der Sonnenkugel statt eigenständig in der Szene: SunTracker.ts setzt die
    // Position direkt auf sunSphereRef/sunLightRef (nicht über setPosition() unten), ein
    // eigenständiges Sprite würde also nie mitwandern. Als Kind folgt es automatisch,
    // ganz ohne SunTracker anzufassen.
    this.mesh.add(this.glow);
  }

  addToScene(scene: THREE.Scene) {
    scene.add(this.light);
    scene.add(this.mesh);
  }

  setPosition(x: number, y: number, z: number) {
    this.light.position.set(x, y, z);
    this.mesh.position.set(x, y, z);
  }
}
